import type { NarrationLine, NarrationTimeline } from "./narrationTimeline";
import type { SyncScene, SyncTimeline } from "./syncTimeline";

// ---------------------------------------------------------------------------
// narrationDrift — how far the narrator is from the visuals the line was
// written for (Director View, DIR-006).
//
// Narration placement is open-loop: the script author writes `start_second`
// against the PLANNED scene timeline, the composer renders scenes at their
// REALIZED lengths (beat snapping, conform, skipped scenes), and nothing
// re-anchors the narration afterwards. So a line keeps its planned offset while
// the images it describes slide away underneath it.
//
// Two different scenes therefore matter per line:
//
//   attributed — the scene whose PLANNED window the line was written into.
//   on screen  — the scene actually visible when the line is REALLY spoken.
//
// When those disagree the viewer hears a line about something they are not
// looking at. The three numbers below quantify it:
//
//   visualShift  = realized.start − planned.start of the attributed scene.
//                  Positive ⇒ the visuals moved later ⇒ the line runs AHEAD of
//                  its images (the "lead" the 2026-07-04 beat-snap echo showed).
//   placement    = actual − intended start of the line itself (the composer's
//                  own overlap push; drift the composer created, not inherited).
//   coverage     = share of the spoken line that overlaps its attributed
//                  scene's realized window. The headline per-line number.
//
// Severity is confidence-gated. A red alarm may only come from timelines that
// are exact on BOTH axes; anything estimated caps at a tilde-prefixed warning,
// because a 1-second "error" against a ±2-second estimate is noise. The one
// exception is an orphaned line — its scene never rendered at all, which is
// structural and true regardless of timing precision.
// ---------------------------------------------------------------------------

/** Composer's own desync thresholds (`qc/orchestrator.py`), reused verbatim. */
export const DRIFT_NOTICEABLE_SECONDS = 0.75;
export const DRIFT_CRITICAL_SECONDS = 1.5;

/** A ramp needs this many consecutive growing scenes to count as systematic. */
const RAMP_MIN_RUN = 5;
/** …and this much accumulated shift, so sub-frame noise never trips it. */
const RAMP_MIN_TOTAL_SECONDS = 2;

export type DriftSeverity = "ok" | "warn" | "critical";

export type NarrationLineDrift = {
  index: number;
  /** Scene the line was written for (planned-window containment). */
  attributedSceneIndex: number | null;
  attributedSceneId: number | string | null;
  /** Scene actually visible while the line is spoken. */
  onScreenSceneIndex: number | null;
  onScreenSceneId: number | string | null;
  /** Realized − planned start of the attributed scene. */
  visualShiftSeconds: number | null;
  /** Actual − intended start of the line. */
  placementDeltaSeconds: number | null;
  /** Fraction of the line spoken over its attributed scene, 0..1. */
  coverageRatio: number | null;
  /** The attributed scene never made the final cut. */
  orphaned: boolean;
  /** No scene window contains the line (opening buffer, tail padding). */
  offScene: boolean;
  /** Attributed and on-screen scenes disagree. */
  onScreenMismatch: boolean;
  severity: DriftSeverity;
  /** Severity came from an estimated timeline — render with a `~` and no red. */
  estimated: boolean;
};

export type DriftCurvePoint = {
  sceneIndex: number;
  /** Delivered-time position of the scene, for plotting. */
  startSeconds: number;
  shiftSeconds: number;
};

export type NarrationDriftSummary = {
  meanCoverageRatio: number | null;
  /** Largest positive visual shift: the worst "line runs ahead" case. */
  maxLeadSeconds: number | null;
  /** Largest negative shift: the worst "line runs behind" case. */
  maxLagSeconds: number | null;
  linesBelowHalfCoverage: number;
  orphanedLineCount: number;
  onScreenMismatchCount: number;
  rampDetected: boolean;
  rampStartSceneIndex: number | null;
  rampTotalSeconds: number | null;
  severity: DriftSeverity;
  estimated: boolean;
};

export type NarrationDriftReport = {
  lines: NarrationLineDrift[];
  curve: DriftCurvePoint[];
  summary: NarrationDriftSummary;
};

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function overlapSeconds(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}

/**
 * Scene whose PLANNED window contains `seconds`. This is the "written for"
 * lookup: it asks where the author thought this moment was, not where it ended
 * up. Returns null when the tier carries no planned bounds.
 */
export function sceneIndexAtPlannedTime(
  scenes: SyncScene[],
  seconds: number,
): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < scenes.length; index += 1) {
    const scene = scenes[index];
    const start = scene.plannedStartSeconds;
    const end = scene.plannedEndSeconds;
    if (start == null || end == null) continue;
    if (seconds >= start && seconds < end) return index;
    // Nearest-window fallback so a line in an opening buffer or a transition
    // seam still attributes somewhere sensible instead of vanishing.
    const distance =
      seconds < start ? start - seconds : seconds >= end ? seconds - end : 0;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  }
  return best;
}

/** Scene whose REALIZED window contains `seconds` (what the viewer sees). */
export function sceneIndexAtRealizedTime(
  scenes: SyncScene[],
  seconds: number,
): number {
  for (let index = 0; index < scenes.length; index += 1) {
    const scene = scenes[index];
    if (scene.skipped) continue;
    const start = scene.startSeconds;
    const end = scene.endSeconds;
    if (start == null || end == null) continue;
    if (seconds >= start && seconds < end) return index;
  }
  return -1;
}

function severityFor(
  magnitudeSeconds: number | null,
  exact: boolean,
): DriftSeverity {
  if (magnitudeSeconds == null) return "ok";
  const magnitude = Math.abs(magnitudeSeconds);
  if (magnitude >= DRIFT_CRITICAL_SECONDS) return exact ? "critical" : "warn";
  if (magnitude >= DRIFT_NOTICEABLE_SECONDS) return "warn";
  return "ok";
}

function worstSeverity(values: DriftSeverity[]): DriftSeverity {
  if (values.includes("critical")) return "critical";
  if (values.includes("warn")) return "warn";
  return "ok";
}

/**
 * Detect the systematic-ramp signature: a run of scenes whose shift grows
 * monotonically. That is what a beat-snap retime looks like — every montage
 * scene stretched by the same fraction of a beat, compounding down the
 * timeline — as opposed to one scene being late.
 */
export function detectRamp(curve: DriftCurvePoint[]): {
  detected: boolean;
  startSceneIndex: number | null;
  totalSeconds: number | null;
} {
  let runStart = 0;
  for (let index = 1; index <= curve.length; index += 1) {
    const continues =
      index < curve.length &&
      curve[index].shiftSeconds > curve[index - 1].shiftSeconds;
    if (continues) continue;
    const runLength = index - runStart;
    if (runLength >= RAMP_MIN_RUN) {
      const total =
        curve[index - 1].shiftSeconds - curve[runStart].shiftSeconds;
      if (total >= RAMP_MIN_TOTAL_SECONDS) {
        return {
          detected: true,
          startSceneIndex: curve[runStart].sceneIndex,
          totalSeconds: round3(total),
        };
      }
    }
    runStart = index;
  }
  return { detected: false, startSceneIndex: null, totalSeconds: null };
}

/**
 * Correlate narration against the realized scene timeline. Pure: the panel and
 * the transcript both render from this one result.
 */
export function buildNarrationDrift(
  narration: NarrationTimeline,
  sync: SyncTimeline,
): NarrationDriftReport {
  // Red requires exactness on both axes — an exact narration timeline laid over
  // rescaled scene estimates still can't prove a 1.5 s error.
  const exact = narration.confidence === "exact" && sync.confidence === "exact";
  const estimated = !exact;

  const lines: NarrationLineDrift[] = narration.lines.map(
    (line: NarrationLine) => {
      const plannedProbe = line.plannedStartSeconds ?? line.startSeconds;
      const attributedIndex = sceneIndexAtPlannedTime(
        sync.scenes,
        plannedProbe,
      );
      const attributed =
        attributedIndex >= 0 ? sync.scenes[attributedIndex] : null;
      const midpoint = (line.startSeconds + line.endSeconds) / 2;
      const onScreenIndex = sceneIndexAtRealizedTime(sync.scenes, midpoint);
      const onScreen = onScreenIndex >= 0 ? sync.scenes[onScreenIndex] : null;

      let visualShiftSeconds: number | null = null;
      if (
        attributed &&
        attributed.startSeconds != null &&
        attributed.plannedStartSeconds != null
      ) {
        visualShiftSeconds = round3(
          attributed.startSeconds - attributed.plannedStartSeconds,
        );
      }

      let coverageRatio: number | null = null;
      const duration = line.endSeconds - line.startSeconds;
      if (
        attributed &&
        attributed.startSeconds != null &&
        attributed.endSeconds != null &&
        duration > 0
      ) {
        coverageRatio = round3(
          overlapSeconds(
            line.startSeconds,
            line.endSeconds,
            attributed.startSeconds,
            attributed.endSeconds,
          ) / duration,
        );
      }

      const orphaned = attributed?.skipped === true;
      const offScene = attributedIndex < 0 || onScreenIndex < 0;
      const onScreenMismatch =
        attributed != null &&
        onScreen != null &&
        attributedIndex !== onScreenIndex;

      const severity = orphaned
        ? "critical"
        : worstSeverity([
            severityFor(visualShiftSeconds, exact),
            // A line pushed far off its intended start is the composer's own
            // desync — the same thing `voiceover_start_shifted` reports.
            severityFor(line.placementDeltaSeconds, exact),
            coverageRatio != null && coverageRatio < 0.5 ? "warn" : "ok",
          ]);

      return {
        index: line.index,
        attributedSceneIndex: attributedIndex >= 0 ? attributedIndex : null,
        attributedSceneId: attributed?.sceneId ?? null,
        onScreenSceneIndex: onScreenIndex >= 0 ? onScreenIndex : null,
        onScreenSceneId: onScreen?.sceneId ?? null,
        visualShiftSeconds,
        placementDeltaSeconds: line.placementDeltaSeconds,
        coverageRatio,
        orphaned,
        offScene,
        onScreenMismatch,
        severity,
        // An orphaned line is structurally true at any tier, so it is never
        // softened to "estimated".
        estimated: estimated && !orphaned,
      };
    },
  );

  const curve: DriftCurvePoint[] = [];
  sync.scenes.forEach((scene, index) => {
    if (
      scene.startSeconds == null ||
      scene.plannedStartSeconds == null ||
      scene.skipped
    ) {
      return;
    }
    curve.push({
      sceneIndex: index,
      startSeconds: scene.startSeconds,
      shiftSeconds: round3(scene.startSeconds - scene.plannedStartSeconds),
    });
  });

  const ramp = detectRamp(curve);
  const coverages = lines
    .map((line) => line.coverageRatio)
    .filter((value): value is number => value != null);
  const shifts = lines
    .map((line) => line.visualShiftSeconds)
    .filter((value): value is number => value != null);
  const leads = shifts.filter((value) => value > 0);
  const lags = shifts.filter((value) => value < 0);

  return {
    lines,
    curve,
    summary: {
      meanCoverageRatio:
        coverages.length > 0
          ? round3(
              coverages.reduce((total, value) => total + value, 0) /
                coverages.length,
            )
          : null,
      maxLeadSeconds: leads.length > 0 ? round3(Math.max(...leads)) : null,
      maxLagSeconds: lags.length > 0 ? round3(Math.min(...lags)) : null,
      linesBelowHalfCoverage: coverages.filter((value) => value < 0.5).length,
      orphanedLineCount: lines.filter((line) => line.orphaned).length,
      onScreenMismatchCount: lines.filter((line) => line.onScreenMismatch)
        .length,
      rampDetected: ramp.detected,
      rampStartSceneIndex: ramp.startSceneIndex,
      rampTotalSeconds: ramp.totalSeconds,
      severity: worstSeverity(lines.map((line) => line.severity)),
      estimated,
    },
  };
}

/** Human-readable one-liner for a line's drift, used in badges and tooltips. */
export function describeLineDrift(drift: NarrationLineDrift): string | null {
  if (drift.orphaned) return "its scene never made the final cut";
  const tilde = drift.estimated ? "~" : "";
  if (
    drift.visualShiftSeconds != null &&
    Math.abs(drift.visualShiftSeconds) >= DRIFT_NOTICEABLE_SECONDS
  ) {
    const magnitude = `${tilde}${Math.abs(drift.visualShiftSeconds).toFixed(1)} s`;
    return drift.visualShiftSeconds > 0
      ? `plays ${magnitude} before its images`
      : `plays ${magnitude} after its images`;
  }
  if (
    drift.placementDeltaSeconds != null &&
    Math.abs(drift.placementDeltaSeconds) >= DRIFT_NOTICEABLE_SECONDS
  ) {
    return `pushed ${tilde}${drift.placementDeltaSeconds.toFixed(1)} s off its written start`;
  }
  if (drift.coverageRatio != null && drift.coverageRatio < 0.5) {
    return `only ${Math.round(drift.coverageRatio * 100)}% of it plays over its scene`;
  }
  return null;
}
